import { useEffect, useRef, useCallback, useState, useMemo, type MutableRefObject } from 'react';
import { ComponentTrackingConfig } from './types';
import { logEvent } from '@eventbrite/legacy-infra.statsig';
import { track } from '@eventbrite/legacy-infra.datalayer-library';

export const DEFAULT_INTERSECTION_OPTIONS: IntersectionObserverInit = {
  root: null,
  threshold: 0, // Trigger as soon as any part is visible for fastest response
  rootMargin: '0px 0px -150px 0px', // 150px above the conversion bar
};

/**
 * Pure visibility hook - detects when a component becomes visible using Intersection Observer.
 *
 * The returned ref is safe to attach immediately, even if the real DOM node appears later because
 * the component renders conditionally or another helper assigns `ref.current` after mount.
 *
 * Timeline:
 * 1. The hook returns a ref object.
 * 2. React (or a helper such as `assignRefsToNode`) assigns a DOM node to `ref.current`.
 * 3. The custom setter mirrors that node into React state.
 * 4. The effect reruns with the real node and starts observing it.
 * 5. The callback fires the first time that node enters the viewport.
 *
 * @param callback - Function to call when component becomes visible
 * @param options - Intersection Observer options
 * @returns Ref to attach to the DOM element
 */
export const useComponentVisibility = (
  callback: () => void,
  options: IntersectionObserverInit = DEFAULT_INTERSECTION_OPTIONS,
) => {
  const elementNodeRef = useRef<HTMLDivElement | null>(null);
  const [observedNode, setObservedNode] = useState<HTMLDivElement | null>(null);
  // hasBeenVisible is used to execute the callback only once
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  const memoizedCallback = useCallback(callback, [callback]);
  const elementRef = useMemo(
    () =>
      ({
        get current() {
          return elementNodeRef.current;
        },
        set current(node: HTMLDivElement | null) {
          if (elementNodeRef.current === node) {
            return;
          }

          elementNodeRef.current = node;

          if (node) {
            setObservedNode(node);
          }
        },
      }) as MutableRefObject<HTMLDivElement | null>,
    [],
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasBeenVisible) {
        memoizedCallback();
        setHasBeenVisible(true);
      }
    }, options);

    const element = observedNode;

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [memoizedCallback, hasBeenVisible, observedNode, options]);

  return elementRef;
};

/**
 * Enhanced visibility hook with tracking capabilities
 *
 * @param callback - Function to call when component becomes visible
 * @param trackingConfig - Optional tracking configuration for Heap/Statsig
 * @returns Ref to attach to the DOM element
 */
export const useComponentVisibilityWithTracking = (callback?: () => void, trackingConfig?: ComponentTrackingConfig) => {
  const memoizedCallback = useCallback(() => {
    callback?.();

    if (trackingConfig) {
      dispatchTrackingEvents(trackingConfig);
    }
  }, [callback, trackingConfig]);

  const options = trackingConfig?.intersectionOptions || DEFAULT_INTERSECTION_OPTIONS;

  return useComponentVisibility(memoizedCallback, options);
};

export const dispatchTrackingEvents = (config: ComponentTrackingConfig) => {
  if (config.heap) {
    track({
      eventName: config.heap.eventName,
      eventData: {
        eventBucketLabel: config.heap.eventBucketLabel,
      },
    });
  }

  if (config.statsig) {
    logEvent(config.statsig.eventName, null, config.statsig.params || null);
  }
};
